import { api } from "@/lib/api";
import { showToast } from "@/lib/toast";
import { t } from "@/lib/strings";
import type { FileRef } from "@/lib/types";

// File attachment / embed helpers shared by the comment composer (RowComments)
// and the long-text rich cell editor (LongTextRichEditor). Images embed inline
// as `![name](url)`; other files as a `[name](url)` download chip.

export const MAX_FILE_BYTES = 100 * 1024 * 1024; // backend cap (settings.max_upload_mb)

// Mirror of backend ALLOWED_UPLOAD_EXTENSIONS (routes/uploads.py). The `accept`
// attribute + this client filter are advisory only — the server re-validates.
export const ACCEPTED_EXTENSIONS = [
  ".png",
  ".jpg",
  ".jpeg",
  ".gif",
  ".webp",
  ".svg",
  ".bmp",
  ".heic",
  ".heif",
  ".pdf",
  ".doc",
  ".docx",
  ".xls",
  ".xlsx",
  ".csv",
  ".ppt",
  ".pptx",
  ".txt",
  ".md",
  ".rtf",
  ".odt",
  ".ods",
  ".odp",
  ".zip",
  // mail (Outlook / MIME)
  ".msg",
  ".eml",
  // video (screen recordings)
  ".mp4",
  ".mov",
  ".webm",
  ".m4v",
];
export const ACCEPT_ATTR = `image/*,${ACCEPTED_EXTENSIONS.join(",")}`;
const IMAGE_NAME_RE = /\.(png|jpe?g|gif|webp|svg|bmp|heic|heif)$/i;

function extensionOf(name: string): string {
  const i = name.lastIndexOf(".");
  return i >= 0 ? name.slice(i).toLowerCase() : "";
}

export function isAcceptedFile(f: File): boolean {
  return (
    f.type.startsWith("image/") ||
    f.type.startsWith("video/") ||
    ACCEPTED_EXTENSIONS.includes(extensionOf(f.name))
  );
}

// An uploaded ref is shown inline as an image when its MIME or name says so;
// everything else gets a file chip + a download link in the rendered output.
export function isImageRef(ref: FileRef): boolean {
  return ref.content_type.startsWith("image/") || IMAGE_NAME_RE.test(ref.name);
}

// Upload one pasted/dropped file for the RichTextEditor's onUploadFile mode
// (replies, edits, and long-text cells — surfaces that have no staged-attachment
// strip; the editor inserts the markdown embed at the cursor). Surfaces the same
// validation toasts as the staged composer; rejects throw so nothing is inserted.
export async function uploadForEditor(
  file: File,
): Promise<{ name: string; url: string; isImage: boolean }> {
  if (!isAcceptedFile(file)) {
    showToast(t.commentFileType, "error");
    throw new Error("unsupported file type");
  }
  if (file.size > MAX_FILE_BYTES) {
    showToast(t.commentFileTooLarge, "error");
    throw new Error("file too large");
  }
  try {
    const ref = await api.uploadFile(file);
    return { name: ref.name, url: ref.url, isImage: isImageRef(ref) };
  } catch (err) {
    showToast(t.commentFileFailed, "error");
    throw err;
  }
}
